import React, { ChangeEvent, useState } from 'react';
import { TDEEField } from './types';
import styles from './styles.module.scss';
import { Button } from 'components/common';

interface TDEECalculatorFormProps {
  onSubmit: (fields: TDEEField) => void;
}

const Field = ({
  label,
  htmlFor,
  children,
}: {
  label: string | React.ReactNode;
  htmlFor: string;
  children: React.ReactNode;
}) => {
  return (
    <div className="tw-relative tw-flex tw-flex-col tw-justify-center tw-gap-4 sm:tw-flex-row sm:tw-justify-start sm:tw-items-center">
      <label
        htmlFor={htmlFor}
        className={`${
          typeof label === 'string' && 'tw-font-brushstrike'
        } tw-text-5xl tw-text-white tw-mb-0 sm:tw-basis-6/12 lg:tw-basis-5/12`}
      >
        {label}
      </label>
      <div className="tw-flex tw-items-center sm:tw-basis-6/12 lg:tw-basis-7/12">
        {children}
      </div>
    </div>
  );
};

const TDEECalculatorForm = ({
  onSubmit,
}: TDEECalculatorFormProps) => {
  const [inputField, setInputField] = useState<TDEEField>({
    gender: undefined,
    weight: undefined,
    height: undefined,
    age: undefined,
    activity: undefined,
  });

  const [isCalculating, setIsCalculating] = useState(false);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setInputField((prev) => ({ ...prev, [name]: value }));
  };

  const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setIsCalculating(true);
      onSubmit(inputField);
    } catch (e) {
      throw e;
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <form
      className="tw-flex tw-flex-col tw-gap-10 md:tw-gap-8 tw-items-stretch"
      onSubmit={handleOnSubmit}
    >
      <Field label="i'm biologically a" htmlFor="gender">
        <div className="tw-flex-auto">
          <input
            className={styles['radioCustom']}
            id="gender-bro"
            type="radio"
            value="bro"
            name="gender"
            required
            checked={inputField.gender === 'bro'}
            onChange={handleInputChange}
          />
          <label
            className="tw-border-[2px] tw-border-white tw-text-white tw-text-2xl tw-font-bold tw-cursor-pointer tw-mb-0 tw-inline-flex tw-p-5 tw-leading-none tw-w-full tw-justify-center"
            htmlFor="gender-bro"
          >
            BRO
          </label>
        </div>

        <div className="tw-flex-auto">
          <input
            className={styles['radioCustom']}
            id="gender-sis"
            type="radio"
            value="sis"
            name="gender"
            checked={inputField.gender === 'sis'}
            onChange={handleInputChange}
          />
          <label
            className="tw-border-[2px] tw-border-white tw-text-white tw-text-2xl tw-font-bold tw-cursor-pointer tw-mb-0 tw-inline-flex tw-p-5 tw-leading-none tw-w-full tw-justify-center"
            htmlFor="gender-sis"
          >
            SIS
          </label>
        </div>
      </Field>
      <Field label="weight:" htmlFor="weight">
        <input
          className={`${styles['input']} tw-border-[2px] tw-outline-none tw-border-white tw-text-white tw-text-2xl focus:tw-border-primary tw-p-4 tw-pr-16 tw-bg-transparent tw-flex-auto tw-rounded-none`}
          required
          onChange={handleInputChange}
          name="weight"
          value={inputField.weight}
          type="number"
        />
        <label
          htmlFor="weightUnit"
          className="tw-text-2xl tw-flex tw-items-center tw-mb-0 tw-absolute tw-right-4 tw-h-[44px] tw-bottom-0 tw-italic tw-text-white"
        >
          kg
        </label>
      </Field>
      <Field label="height:" htmlFor="height">
        <input
          className={`${styles['input']} tw-border-[2px] tw-outline-none tw-border-white tw-text-white tw-text-2xl focus:tw-border-primary tw-p-4 tw-pr-16 tw-bg-transparent tw-flex-auto tw-rounded-none`}
          required
          onChange={handleInputChange}
          name="height"
          value={inputField.height}
          type="number"
        />
        <label
          htmlFor="heightUnit"
          className="tw-text-2xl tw-flex tw-items-center tw-mb-0 tw-absolute tw-right-4 tw-h-[44px] tw-bottom-0 tw-italic tw-text-white"
        >
          cm
        </label>
      </Field>
      <Field label="age:" htmlFor="age">
        <input
          className={`${styles['input']} tw-border-[2px] tw-outline-none tw-border-white tw-text-white tw-text-2xl focus:tw-border-primary tw-p-4 tw-pr-24 tw-bg-transparent tw-flex-auto tw-rounded-none`}
          required
          onChange={handleInputChange}
          name="age"
          value={inputField.age}
          type="number"
        />
        <label
          htmlFor="ageUnit"
          className="tw-text-2xl tw-flex tw-items-center tw-mb-0 tw-absolute tw-right-4 tw-h-[44px] tw-bottom-0 tw-italic tw-text-white"
        >
          years
        </label>
      </Field>
      <Field
        label={
          <React.Fragment>
            <span className="tw-font-brushstrike tw-text-5xl tw-text-white">
              physical activity:
            </span>
            <span className="tw-italic tw-text-2xl tw-block tw-mt-1">
              Don't lie to yourself!
            </span>
          </React.Fragment>
        }
        htmlFor="activity"
      >
        <select
          className={`${styles['select']} tw-w-full tw-border-[2px] tw-outline-none tw-border-white tw-text-2xl tw-p-4 tw-bg-transparent tw-flex-auto`}
          required
          onChange={handleInputChange}
          name="activity"
          value={inputField.activity}
        >
          <option value="" disabled selected>
            Select honest answer
          </option>
          <option value={1.2}>Little or No Exercise</option>
          <option value={1.375}>
            Light exercise (Sports or Cardio 1-3 days per week)
          </option>
          <option value={1.55}>
            Moderate exercise (Sports or Cardio 3-5 days per week)
          </option>
          <option value={1.725}>
            Hard exercise (Sports or Cardio 6-7 days per week)
          </option>
          <option value={1.9}>
            Very hard exercise (Sports or Cardio & Physical Job or 2x
            training per day)
          </option>
        </select>
      </Field>
      <div className="tw-flex">
        {/* @ts-ignore */}
        <Button
          type="submit"
          loading={isCalculating}
          disabled={isCalculating}
          className="tw-uppercase tw-w-full py-3"
        >
          <span>Do Math Magic</span>
          <i className="icon-long-arrow-right"></i>
        </Button>
      </div>
    </form>
  );
};

export default TDEECalculatorForm;
